import { Inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  APP_TENANT_CONFIG,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { findTenantConfigByHostname } from '../../core/states/tenant-state/find-tenant-config-by-hostname';

const MAIN_FLINK_APP_BUNDLE_ID = 'ch.ce.wink';

@Injectable({
  providedIn: 'root'
})
export class CanSelectTenantGuard {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private store: Store,
    @Inject(APP_TENANT_CONFIG) private tenantConfigs: TenantConfig[]
  ) {}

  private async isMainApp(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return !findTenantConfigByHostname(
        this.tenantConfigs,
        window.location.hostname
      );
    }
    const appInfo = await App.getInfo();
    return (
      appInfo.id === MAIN_FLINK_APP_BUNDLE_ID ||
      appInfo.id == `${MAIN_FLINK_APP_BUNDLE_ID}.staging`
    );
  }

  private async hasResolvedTenant(): Promise<boolean> {
    const tenant = await firstValueFrom(
      this.store.select(getCurrentTenant).pipe(filter(t => t !== 'loading'))
    );
    return !!tenant;
  }

  async canActivateChild(): Promise<boolean | UrlTree> {
    const isLoggedIn = await firstValueFrom(
      this.authenticationService.isLoggedIn()
    );
    if (isLoggedIn) {
      return this.router.parseUrl('/');
    }

    if (await this.isMainApp()) {
      return true;
    }
    return (await this.hasResolvedTenant())
      ? this.router.createUrlTree(['/login'])
      : true;
  }
}
